import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Plus, Trash2, Check, Loader2 } from 'lucide-react'
import { useCreatePurchaseOrder } from '@/hooks/procurement'
import { useInventoryItems } from '@/hooks/inventory'
import { useSuppliers } from '@/hooks/basic-information'
import { getApiErrorMessage } from '@/api/client'
import { useToast } from '@/components/ToastProvider'

interface PurchaseItem {
  skuCode: string
  partName: string
  specification: string
  quantity: number
  unit: string
}

interface PreselectedState {
  preselectedItems?: Array<{ skuCode: string; partName?: string; quantity?: number }>
  source?: 'inventory-alert' | 'inventory-replenishment'
}

const WAREHOUSES = ['MAIN', 'BACKUP']

export default function PurchaseCreate() {
  const navigate = useNavigate()
  const location = useLocation()
  const { workOrderId } = useParams()
  const workOrderParam = workOrderId ? parseInt(workOrderId, 10) : undefined
  const toast = useToast()

  const createMutation = useCreatePurchaseOrder()
  const { data: inventoryItems } = useInventoryItems()
  const { data: suppliers, isLoading: suppliersLoading } = useSuppliers()

  const routeState = (location.state ?? {}) as PreselectedState
  const isInventoryReplenishment =
    routeState.source === 'inventory-alert' || routeState.source === 'inventory-replenishment'
  const [supplierId, setSupplierId] = useState<number>()
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('')
  const [warehouseCode, setWarehouseCode] = useState('MAIN')
  const [remark, setRemark] = useState('')
  const [items, setItems] = useState<PurchaseItem[]>([])
  const [selectedSkuCode, setSelectedSkuCode] = useState('')
  const [selectedQuantity, setSelectedQuantity] = useState(1)

  useEffect(() => {
    if (!inventoryItems || !routeState.preselectedItems?.length) return
    setItems((current) => {
      if (current.length > 0) return current
      return routeState.preselectedItems!
        .map((preselected) => {
          const inventory = inventoryItems.find((item) => item.skuCode === preselected.skuCode)
          if (!inventory) return null
          return {
            skuCode: inventory.skuCode,
            partName: inventory.partName,
            specification: inventory.specification || '',
            quantity: Math.max(1, preselected.quantity ?? 1),
            unit: inventory.unit,
          }
        })
        .filter((item): item is PurchaseItem => !!item)
    })
  }, [inventoryItems, routeState.preselectedItems])

  const totalItems = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items]
  )
  const selectedSupplier = useMemo(
    () => suppliers?.find((supplier) => supplier.id === supplierId),
    [supplierId, suppliers]
  )

  const handleAddSelectedItem = () => {
    if (!selectedSkuCode) return
    const inventory = inventoryItems?.find((item) => item.skuCode === selectedSkuCode)
    if (!inventory) return

    setItems((currentItems) => {
      const targetIndex = currentItems.findIndex((item) => item.skuCode === selectedSkuCode)
      if (targetIndex >= 0) {
        return currentItems.map((item, index) =>
          index === targetIndex ? { ...item, quantity: selectedQuantity } : item
        )
      }
      return [
        ...currentItems,
        {
          skuCode: inventory.skuCode,
          partName: inventory.partName,
          specification: inventory.specification || '',
          quantity: selectedQuantity,
          unit: inventory.unit,
        },
      ]
    })

    setSelectedSkuCode('')
    setSelectedQuantity(1)
  }

  const handleQuantityChange = (index: number, quantity: number) => {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    )
  }

  const handleRemoveItem = (index: number) => {
    setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleSubmit = async () => {
    if (!warehouseCode.trim()) {
      toast.error('请选择收货仓库')
      return
    }
    if (items.length === 0) {
      toast.error('请至少添加一个采购配件')
      return
    }

    try {
      const result = await createMutation.mutateAsync({
        workOrderId: workOrderParam,
        supplierId,
        expectedDeliveryDate: expectedDeliveryDate || undefined,
        warehouseCode,
        remark: remark.trim() || undefined,
        items: items.map((item) => ({
          skuCode: item.skuCode,
          partName: item.partName,
          specification: item.specification || undefined,
          quantity: item.quantity,
          unit: item.unit,
        })),
      })
      toast.success(isInventoryReplenishment ? '补货申请已提交，等待采购审批' : '采购单创建成功')
      navigate(`/supply-chain/purchase/${result.id}`)
    } catch (error) {
      toast.error(getApiErrorMessage(error))
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/supply-chain/purchase/list" className="rounded-lg p-2 transition hover:bg-gray-100">
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isInventoryReplenishment ? '创建补货申请' : '创建采购单'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {isInventoryReplenishment
                ? '根据库存情况预填物料和建议数量，提交后进入采购审批'
                : workOrderParam
                  ? `为工单 ${workOrderParam} 创建采购`
                  : '创建独立采购单'}
            </p>
          </div>
        </div>
        <span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-600">
          {isInventoryReplenishment ? '补货申请' : '草稿'}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          {workOrderParam ? (
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="border-b border-gray-100 p-5">
                <h2 className="text-lg font-bold text-gray-900">关联工单</h2>
              </div>
              <div className="p-5">
                <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500">
                      <Plus className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">工单 #{workOrderParam}</div>
                      <div className="text-xs text-gray-500">采购将回写到工单闭环</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 p-5">
              <h2 className="text-lg font-bold text-gray-900">供应商信息</h2>
            </div>
            <div className="p-5">
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  供应商 <span className="font-normal text-gray-400">（可选）</span>
                </label>
                <select
                  value={supplierId ?? ''}
                  onChange={(event) => setSupplierId(event.target.value ? Number(event.target.value) : undefined)}
                  disabled={suppliersLoading}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">不指定供应商</option>
                  {suppliers?.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.supplierName}{supplier.city ? ` · ${supplier.city}` : ''}</option>)}
                </select>
                {selectedSupplier ? <div className="mt-3 rounded-xl bg-gray-50 p-3 text-sm text-gray-600"><span className="font-medium text-gray-800">{selectedSupplier.contactPerson || '暂无联系人'}</span><span className="mx-2 text-gray-300">|</span>{selectedSupplier.contactPhone || '暂无联系电话'}{selectedSupplier.contactAddress ? <div className="mt-1 text-xs text-gray-500">{selectedSupplier.contactAddress}</div> : null}</div> : null}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">预计交付日期</label>
                  <input
                    type="date"
                    value={expectedDeliveryDate}
                    onChange={(event) => setExpectedDeliveryDate(event.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">收货仓库</label>
                  <select
                    value={warehouseCode}
                    onChange={(event) => setWarehouseCode(event.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {WAREHOUSES.map((warehouse) => (
                      <option key={warehouse} value={warehouse}>
                        {warehouse}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 p-5">
              <h2 className="text-lg font-bold text-gray-900">
                采购配件 <span className="text-red-500">*</span>
              </h2>
            </div>
            <div className="p-5">
              {items.length === 0 ? (
                <div className="py-8 text-center text-gray-400">请添加采购配件</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-sm text-gray-500">
                      <th className="pb-3 font-medium">配件信息</th>
                      <th className="pb-3 font-medium">规格型号</th>
                      <th className="pb-3 text-center font-medium">采购数量</th>
                      <th className="pb-3 font-medium">单位</th>
                      <th className="pb-3 font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {items.map((item, index) => (
                      <tr key={item.skuCode} className="border-b border-gray-100">
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                              <span className="text-lg">备</span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{item.partName}</div>
                              <div className="text-xs text-gray-400">{item.skuCode}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 text-gray-600">{item.specification || '-'}</td>
                        <td className="py-4 text-center">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(event) =>
                              handleQuantityChange(index, parseInt(event.target.value, 10) || 1)
                            }
                            className="w-16 rounded-lg border border-gray-200 py-1.5 text-center text-sm"
                          />
                        </td>
                        <td className="py-4 text-gray-600">{item.unit}</td>
                        <td className="py-4">
                          <button
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-500 transition hover:text-red-700"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="mt-4 border-t border-gray-100 pt-4">
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={selectedSkuCode}
                    onChange={(event) => setSelectedSkuCode(event.target.value)}
                    className="min-w-0 flex-[1_1_20rem] rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">选择配件...</option>
                    {inventoryItems?.map((item) => (
                      <option key={item.skuCode} value={item.skuCode}>
                        {item.partName} ({item.skuCode}) - 库存: {item.availableQty}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={selectedQuantity}
                    onChange={(event) =>
                      setSelectedQuantity(Math.max(1, parseInt(event.target.value, 10) || 1))
                    }
                    className="w-20 rounded-xl border border-gray-200 px-3 py-2.5 text-center text-sm"
                  />
                  <button
                    onClick={handleAddSelectedItem}
                    className="rounded-xl bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-600 transition hover:bg-primary-100"
                  >
                    添加
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 p-5">
              <h2 className="text-lg font-bold text-gray-900">备注说明</h2>
            </div>
            <div className="p-5">
              <textarea
                value={remark}
                onChange={(event) => setRemark(event.target.value)}
                placeholder="请输入备注信息（可选）"
                rows={3}
                className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 p-5">
              <h2 className="text-lg font-bold text-gray-900">采购汇总</h2>
            </div>
            <div className="space-y-4 p-5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">配件种类</span>
                <span className="font-medium text-gray-900">{items.length} 种</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">配件数量</span>
                <span className="font-medium text-gray-900">{totalItems} 件</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">供应商</span>
                <span className="font-medium text-gray-900">{selectedSupplier?.supplierName || '未指定'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">收货仓库</span>
                <span className="font-medium text-gray-900">{warehouseCode}</span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-5">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500">
                <span className="text-xl text-white">i</span>
              </div>
              <div>
                <div className="font-bold text-gray-900">温馨提示</div>
                <div className="text-sm text-gray-600">创建后需要审批才能正式下单</div>
              </div>
            </div>
            <div className="rounded-lg bg-white p-3 text-sm text-gray-700">
              采购单创建后会进入审批流，审批通过后采购人员再执行下单与收货。
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-3 text-sm font-bold text-white transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {createMutation.isPending ? '提交中...' : '提交审批'}
            </button>
            <Link
              to="/supply-chain/purchase/list"
              className="block w-full rounded-xl border border-gray-200 bg-white py-3 text-center text-sm font-medium text-gray-600 transition hover:bg-gray-50"
            >
              取消
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
